import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Users, Loader2 } from "lucide-react";
import { api, type ApiResponse } from "@/api/client";

interface PaxRow {
  id: string;
  participantId: string;
  firstName: string;
  lastName: string;
  paidAt: string | null;
  issuedAt: string | null;
  deliveredToGuideAt: string | null;
  checkedAt: string | null;
}

const STAMPS: Array<{ field: string; label: string; key: keyof PaxRow }> = [
  { field: "paid", label: "Paid", key: "paidAt" },
  { field: "issued", label: "Issued", key: "issuedAt" },
  { field: "delivered", label: "To guide", key: "deliveredToGuideAt" },
  { field: "checked", label: "Checked", key: "checkedAt" },
];

/**
 * Per-pax fulfilment grid for one per-pax service booking (Block 5.7 Řez 1 — replaces
 * the TOURS/TICKET CHECK sheets). Collapsed by default; rows materialise lazily on
 * first open. "Checked" pre-fills from the ops passport check (5.2).
 */
export default function ServiceBookingPaxGrid({ bookingId }: { bookingId: string }) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["sb-pax", bookingId],
    queryFn: () => api.get<ApiResponse<PaxRow[]>>(`/api/admin/service-bookings/${bookingId}/pax`),
    enabled: open,
  });
  const toggle = useMutation({
    mutationFn: (vars: { paxId: string; field: string; value: boolean }) =>
      api.patch(`/api/admin/service-bookings/${bookingId}/pax/${vars.paxId}`, {
        field: vars.field,
        value: vars.value,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sb-pax", bookingId] }),
  });

  const rows = data?.data ?? [];

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
      >
        <Users className="h-3.5 w-3.5" /> {open ? "Hide pax grid" : "Pax grid"}
      </button>
      {open && (
        <div className="mt-2 overflow-x-auto rounded border border-gray-200 dark:border-gray-700">
          {isLoading ? (
            <div className="flex items-center gap-2 p-3 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <p className="p-3 text-sm text-gray-400">
              No travellers yet — add participants to the order first.
            </p>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr className="text-left text-xs uppercase text-gray-500">
                  <th className="px-3 py-2">Traveller</th>
                  {STAMPS.map((s) => (
                    <th key={s.field} className="px-3 py-2 text-center">
                      {s.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="whitespace-nowrap px-3 py-1.5 text-gray-900 dark:text-white">
                      {r.lastName} {r.firstName}
                    </td>
                    {STAMPS.map((s) => {
                      const stamped = !!r[s.key];
                      return (
                        <td key={s.field} className="px-3 py-1.5 text-center">
                          <input
                            type="checkbox"
                            checked={stamped}
                            disabled={toggle.isPending}
                            onChange={() =>
                              toggle.mutate({ paxId: r.id, field: s.field, value: !stamped })
                            }
                            className="text-primary-600 h-4 w-4 rounded"
                            title={stamped ? String(r[s.key]).slice(0, 10) : s.label}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
